import { Add as AddIcon } from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import { useCallback, useState } from "react";

import { Dialog } from "../../atoms/Dialog/Dialog";
import { Form, type FormProps, ResetButton, SubmitButton, type TextFieldValue } from "../../atoms/Form";
import {
  Description,
  FIELD_NAME_DESCRIPTION,
  FIELD_NAME_RESOURCE_NAME,
  FIELD_NAME_URL,
  ResourceName,
  Url,
} from "../../molecules/Form";

// Centralized strings for internationalization
const tAddNewResource = "Add New Resource";
const tCancel = "Cancel";
const tCreate = "Create Resource";

const requiredFields = [FIELD_NAME_RESOURCE_NAME, FIELD_NAME_URL];

interface FormValues {
  [FIELD_NAME_RESOURCE_NAME]: TextFieldValue;
  [FIELD_NAME_DESCRIPTION]: TextFieldValue;
  [FIELD_NAME_URL]: TextFieldValue;
}

interface NewResource {
  name: string;
  description: string;
  url: string;
}

interface ResourceFormDialogProps {
  onAddResource: (resource: NewResource) => void;
}

export const ResourceFormDialog = ({ onAddResource }: ResourceFormDialogProps) => {
  const [open, setOpen] = useState(false);

  const closeDialog = useCallback((form?: { reset: () => void }) => {
    setOpen(false);
    form?.reset();
  }, []);

  const openDialog = useCallback(() => setOpen(true), []);

  const handleSubmit = useCallback<FormProps<FormValues>["onSubmit"]>(
    (formValues: FormValues, form) => {
      const name = formValues[FIELD_NAME_RESOURCE_NAME];
      const url = formValues[FIELD_NAME_URL];

      if (name && url) {
        const resource: NewResource = {
          description: formValues[FIELD_NAME_DESCRIPTION] || "",
          name,
          url,
        };

        onAddResource(resource);
        closeDialog(form);
      }
    },
    [onAddResource, closeDialog],
  );

  return (
    <>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={openDialog}
        aria-label="Add new resource"
        fullWidth
        sx={{
          "&:hover": {
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.info.dark} 0%, ${theme.palette.info.dark} 100%)`,
          },
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
          borderRadius: "12px",
          color: "white",
          fontWeight: "bold",
          textTransform: "none",
        }}
      >
        {tAddNewResource}
      </Button>

      <Form<FormValues> onSubmit={handleSubmit}>
        {({ form }) => (
          <Dialog
            actions={
              <Box display="grid" gap="2rem" gridAutoColumns="minmax(0, 1fr)" gridAutoFlow="row" padding="1rem">
                <ResourceName />
                <Description maxLength={250} />
                <Url />
                <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                  <ResetButton onClick={() => closeDialog(form)}>{tCancel}</ResetButton>
                  <SubmitButton requiredFields={requiredFields} aria-label="Create resource">
                    {tCreate}
                  </SubmitButton>
                </Box>
              </Box>
            }
            dialogType="custom"
            maxWidth="sm"
            onDialogClose={() => closeDialog(form)}
            open={open}
            title={tAddNewResource}
          />
        )}
      </Form>
    </>
  );
};
